/**
 * 交互反馈打点（任务 20c）：掐「用户动作 → 屏幕上出现反馈」的那一段。
 *
 * 与启动打点（marks.ts）的区别：启动是**一次性里程碑**（markOnce 同名只打一次），
 * 交互是**可重复的区间**，所以这里是 span：动作入口 `begin`、反馈渲染处 `end`，
 * 产出一条 `performance.measure`。真机与 e2e 读同一组 measure（口径见 docs/perf.md）。
 *
 * 三条约定，改之前先读：
 *
 * 1. **end 写在渲染路径里，不写在 effect 里**。effect 在提交之后才跑，会把
 *    「反馈已决定」晚算一帧甚至几帧（有的 effect 还会被 setState 连锁推后）。
 *    与 `lh:interactive` 同款刻意偏差：量到的是「这一帧的 DOM 已决定」，比像素
 *    真正上屏早不到一帧。
 * 2. **同一 kind 同时只有一个在飞的 span**：再 `begin` 一次直接盖掉前一个
 *    （用户一次只做一个动作；没等到反馈就又点了的那次，旧的数已经没有意义）。
 * 3. **token 必须严格对上才收**。token 一般是会话 key：切会话时 A 的 span 不能
 *    被 B 的渲染结掉。两端都省略即空串对空串（发消息路径没有天然 token）。
 *
 * 条目数量是有界的：每次 `end` 先清掉同名的旧 measure 与自己的 start mark，所以
 * 一个 kind 在 buffer 里最多留一条最新的。e2e 采样前自己 `clearMeasures()`，拿到
 * 的就是本次采样那一条。
 */

/** 可测量的交互种类（名字进 measure 条目名，e2e 与 docs/perf.md 按此对齐） */
export const INTERACTIONS = {
  /** 从列表进入一个会话（栈顶不是聊天页时的进入） */
  openChat: 'open-chat',
  /** 已在会话 A 的情况下切到会话 B（桌面双栏的直接切换） */
  switchChat: 'switch-chat',
  /** 上面两条动作的后续里程碑：目标会话的消息行真正渲染出来（含读库） */
  chatRows: 'chat-rows',
  /** 点发送 → 乐观气泡出现在列表里 */
  sendMessage: 'send-message',
} as const;

export type InteractionKind = (typeof INTERACTIONS)[keyof typeof INTERACTIONS];

/** measure 条目名前缀；e2e 按此前缀筛（`lh:` 避免与第三方打点撞名） */
export const INTERACTION_MEASURE_PREFIX = 'lh:i:';
const START_MARK_PREFIX = 'lh:i-start:';

export function interactionMeasureName(kind: InteractionKind): string {
  return `${INTERACTION_MEASURE_PREFIX}${kind}`;
}

function startMarkName(kind: InteractionKind): string {
  return `${START_MARK_PREFIX}${kind}`;
}

/** 在飞的 span：kind → token（'' = 不校验 token） */
const pending = new Map<InteractionKind, string>();

function asList(kinds: InteractionKind | readonly InteractionKind[]): readonly InteractionKind[] {
  return typeof kinds === 'string' ? [kinds] : kinds;
}

/**
 * 动作入口打点。一次动作可能同时开几个里程碑的 span（进会话 = `open-chat` 壳
 * 出现 + `chat-rows` 消息到位），它们共用同一个起点。
 *
 * 失败一律静默：打点是观测手段，不能把页面搞崩（marks.ts 同口径）。
 */
export function beginInteraction(
  kinds: InteractionKind | readonly InteractionKind[],
  token = '',
): void {
  for (const kind of asList(kinds)) {
    pending.set(kind, token);
    try {
      performance.clearMarks(startMarkName(kind));
      performance.mark(startMarkName(kind));
    } catch {
      // User Timing 不可用（极旧 WebView）：观测缺失可以接受，报错不行
    }
  }
}

/**
 * 反馈渲染处打点。没有在飞的 span（或 token 不符）就什么都不做——所以可以无脑
 * 放在渲染路径里，重渲染不会产出假数据。
 *
 * 返回本次量到的毫秒数（没结到任何 span 返回 null），方便单测与调试。
 */
export function endInteraction(
  kinds: InteractionKind | readonly InteractionKind[],
  token = '',
): number | null {
  let last: number | null = null;
  for (const kind of asList(kinds)) {
    const expected = pending.get(kind);
    if (expected === undefined) continue;
    // token 必须严格对上：切会话时 A 的 span 不能被 B 的渲染结掉
    if (expected !== token) continue;
    pending.delete(kind);
    const name = interactionMeasureName(kind);
    try {
      performance.clearMeasures(name); // 每个 kind 只留最新一条，条目不无界增长
      const entry = performance.measure(name, startMarkName(kind));
      performance.clearMarks(startMarkName(kind));
      // 老浏览器的 measure() 返回 undefined（User Timing L2），此时只能回读条目
      const duration =
        entry?.duration ??
        (performance.getEntriesByName(name, 'measure')[0]?.duration as number | undefined);
      if (duration !== undefined) last = duration;
    } catch {
      // 起点标记已被清（重复 end）或 User Timing 不可用：无数据，不是错误
    }
  }
  return last;
}

/** 仅供单测：清掉模块级在飞状态 */
export function resetInteractionsForTest(): void {
  pending.clear();
}
